import * as R from 'ramda';
import { createSelector } from 'reselect';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
//////////////////////////////////////////////////

const store = ({ customerMasterInvoice }) => customerMasterInvoice;
const configStore = ({ config }) => config;

const makeSelectDocumentTypeOptions = () => createSelector(
  configStore,
  (state) => R.pathOr([], ['invoice', 'options', 'availableDocumentTypes'], state),
);

const makeSelectInvoiceStatusOptions = () => createSelector(
  configStore,
  (state) => R.pathOr([], ['invoice', 'options', 'customerInvoiceStatuses'], state),
);

const makeSelectReportStatus = () => createSelector(
  store,
  ({ reportPending }) => reportPending,
);

const makeSelectAvailableReports = () => createSelector(
  store,
  ({ availableReports }) => availableReports,
);

const makeSelectUsedReport = () => createSelector(
  store,
  ({ usedReport }) => usedReport,
);

const makeSelectFilterParams = () => createSelector(
  store,
  ({ filterParams }) => filterParams,
);

const makeSelectTableTitleFilters = () => createSelector(
  store,
  ({ tableTitleFilters }) => tableTitleFilters,
);

const makeSelectTitleSortValues = () => createSelector(
  store,
  ({ titleSortValues }) => titleSortValues,
);

const makeSelectItemList = () => createSelector(
  store,
  ({ itemList }) => R.sortBy(
    R.prop('index'),
    R.values(itemList),
  ),
);

const makeSelectPagination = () => createSelector(
  store,
  ({ pagination }) => pagination,
);

const makeSelectTotalCount = () => createSelector(
  store,
  ({ totalCount }) => totalCount,
);

const makeSelectListLoading = () => createSelector(
  store,
  ({ loading }) => loading,
);

const makeSelectMasterInvoiceConfigs = () => createSelector(
  store,
  ({ currentBranchConfigs }) => {
    const autoNumber = G.getConfigValueFromStore(
      GC.INVOICE_CLO_MASTER_INVOICE_NUMBER_AUTOGENERATED,
      currentBranchConfigs,
    );

    const dateRange = G.getConfigValueFromStore(
      GC.INVOICE_CLO_MASTER_INVOICE_DATE_RANGE_DAYS,
      currentBranchConfigs,
      30,
    );

    const invoiceFilterDateRange = G.getConfigValueFromStore(
      GC.INVOICE_CLO_MASTER_INVOICE_INVOICE_FILTER_DATE_RANGE_DAYS,
      currentBranchConfigs,
      30,
    );

    const defaultDocumentTypeGuids = G.getConfigValueFromStore(
      GC.INVOICE_CLO_MASTER_INVOICE_DEFAULT_DOCUMENT_TYPES,
      currentBranchConfigs,
      [],
    );

    const defaultInvoiceStatusGuids = G.getConfigValueFromStore(
      GC.INVOICE_CLO_MASTER_INVOICE_DEFAULT_STATUS,
      currentBranchConfigs,
      [],
    );

    const defaultInvoicePaymentTerms = G.getConfigValueFromStore(
      GC.INVOICE_CLO_MASTER_INVOICE_DEFAULT_INVOICE_PAYMENT_TERMS,
      currentBranchConfigs,
    );

    const groupByReference = G.getConfigValueFromStore(
      GC.INVOICE_CLO_MASTER_INVOICE_GROUP_BY_REFERENCE,
      currentBranchConfigs,
    );

    const carrierDocumentTypeOptions = G.createOptionsFromDropdownConfigWithGuidOrParentGuid(
      currentBranchConfigs,
      GC.CARRIER_DOCUMENT_TYPE,
    );

    const defaultGroupBy = G.getConfigValueFromStore(
      GC.INVOICE_CLO_MASTER_INVOICE_DEFAULT_GROUP_BY,
      currentBranchConfigs,
    );

    const defaultCarrierDocumentTypeGuids = G.getConfigValueFromStore(
      GC.INVOICE_CLO_MASTER_INVOICE_DEFAULT_CARRIER_DOCUMENT_TYPES,
      currentBranchConfigs,
      [],
    );

    const defaultMasterInvoiceTemplate = G.getConfigValueFromStore(
      GC.INVOICE_CLO_MASTER_INVOICE_PRINT_MASTER_INVOICE_DEFAULT_TEMPLATE,
      currentBranchConfigs,
    );

    const defaultTemplate = G.getConfigValueFromStore(
      GC.INVOICE_CLO_MASTER_INVOICE_DEFAULT_TEMPLATE,
      currentBranchConfigs,
    );

    const defaultFactoringName = G.getConfigValueFromStore(
      GC.INVOICE_CLO_MASTER_INVOICE_DEFAULT_FACTORING_NAME,
      currentBranchConfigs,
    );

    const defaultSendEmails = G.getConfigValueFromStore(
      GC.INVOICE_CLO_MASTER_INVOICE_DEFAULT_SEND_EMAILS,
      currentBranchConfigs,
    );

    const defaultReplyTo = G.getConfigValueFromStore(
      GC.INVOICE_CLO_MASTER_INVOICE_DEFAULT_REPLY_TO,
      currentBranchConfigs,
    );

    const qbAuthorization = G.getConfigValueFromStore(
      GC.INVOICE_QB_AUTHORIZATION,
      currentBranchConfigs,
    );

    return {
      dateRange,
      autoNumber,
      defaultGroupBy,
      defaultReplyTo,
      defaultTemplate,
      qbAuthorization,
      groupByReference,
      defaultSendEmails,
      defaultFactoringName,
      invoiceFilterDateRange,
      defaultDocumentTypeGuids,
      defaultInvoiceStatusGuids,
      defaultInvoicePaymentTerms,
      carrierDocumentTypeOptions,
      defaultMasterInvoiceTemplate,
      defaultCarrierDocumentTypeGuids,
    };
  },
);

const makeSelectUpdatedInvoice = () => createSelector(
  store,
  ({ updatedInvoice }) => updatedInvoice,
);

const makeSelectAvailableFactoringTypes = () => createSelector(
  store,
  ({ availableFactoringTypes }) => availableFactoringTypes,
);

const makeSelectRecallDocumentsGuid = () => createSelector(
  store,
  ({ recallDocumentsGuid }) => recallDocumentsGuid,
);

const makeSelectAvailableCustomExportOptions = () => createSelector(
  store,
  ({ availableCustomExportTypes }) => {
    const options = [
      {
        label: 'MLM Order Master Invoice',
        value: GC.CUSTOM_EXCEL_EXPORT_TYPE_MLM_CLO_MASTER_INVOICE,
      },
      {
        label: 'NAL Order Master Invoice',
        value: GC.CUSTOM_EXCEL_EXPORT_TYPE_NAL_CLO_MASTER_INVOICE,
      },
      {
        label: 'xTeamAir Excel',
        value: GC.CUSTOM_EXCEL_EXPORT_TYPE_NAL_CLO_MASTER_INVOICE_X_TEAM_AIR,
      },
    ];

    const availableOptions = R.filter(
      ({ value }) => R.includes(value, R.or(availableCustomExportTypes, [])),
      options,
    );

    return availableOptions;
  },
);

export {
  makeSelectItemList,
  makeSelectUsedReport,
  makeSelectPagination,
  makeSelectTotalCount,
  makeSelectListLoading,
  makeSelectReportStatus,
  makeSelectFilterParams,
  makeSelectUpdatedInvoice,
  makeSelectTitleSortValues,
  makeSelectAvailableReports,
  makeSelectTableTitleFilters,
  makeSelectDocumentTypeOptions,
  makeSelectRecallDocumentsGuid,
  makeSelectMasterInvoiceConfigs,
  makeSelectInvoiceStatusOptions,
  makeSelectAvailableFactoringTypes,
  makeSelectAvailableCustomExportOptions,
};
